import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { QRCodeCanvas, QRCodeSVG } from 'qrcode.react';
import {
  Box,
  Button,
  Checkbox,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Slider,
  Stack,
  TextField,
  Typography,
  createTheme,
  ThemeProvider,
  CssBaseline,
} from '@mui/material';
import mosquito from '../Assets/Images/ObesquitoMosquito.png';

/** The four levels the QR spec defines, ordered by how much damage they absorb. */
type ErrorCorrectionLevel = 'L' | 'M' | 'Q' | 'H';

/** qrcode.react does not export its image settings type, so mirror what we pass. */
type LogoSettings = {
  src: string;
  height: number;
  width: number;
  excavate: boolean;
};

const theme = createTheme({
  palette: {
    mode: 'dark',
    background: { default: '#282c34', paper: '#31363f' },
  },
});

const LEVELS: ReadonlyArray<{ value: ErrorCorrectionLevel; label: string }> = [
  { value: 'L', label: 'L - Low (7%)' },
  { value: 'M', label: 'M - Medium (15%)' },
  { value: 'Q', label: 'Q - Quartile (25%)' },
  { value: 'H', label: 'H - High (30%)' },
];

/**
 * Strips characters that are unsafe in a file name, so the download gets a name
 * derived from what was actually encoded.
 */
function fileNameFor(value: string): string {
  const base = value
    .replace(/^https?:\/\//i, '')
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
  return base ? `obesquito-qr-${base}` : 'obesquito-qr';
}

function triggerDownload(href: string, fileName: string): void {
  const link = document.createElement('a');
  link.href = href;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function QrCodeGeneratorPage(): JSX.Element {
  const [value, setValue] = useState<string>('https://obesquito.itch.io/mark-of-the-knights');
  const [size, setSize] = useState<number>(320);
  const [level, setLevel] = useState<ErrorCorrectionLevel>('H');
  const [fgColor, setFgColor] = useState<string>('#000000');
  const [bgColor, setBgColor] = useState<string>('#ffffff');
  const [quietZone, setQuietZone] = useState<boolean>(true);
  const [withLogo, setWithLogo] = useState<boolean>(true);
  // The bundled logo is inlined as a data URI by CRA when it is small enough,
  // but not when it is not - reading it into a data URI ourselves keeps the
  // downloaded SVG self contained either way.
  const [logoDataUri, setLogoDataUri] = useState<string | null>(null);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  // Unlisted page: keep it out of search indexes even if the URL leaks.
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    const previousTitle = document.title;
    document.title = 'QR Generator - Obesquito';
    return () => {
      document.head.removeChild(meta);
      document.title = previousTitle;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch(mosquito)
      .then((res) => res.blob())
      .then(
        (blob) =>
          new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            // readAsDataURL always yields a string, unlike readAsArrayBuffer
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(blob);
          })
      )
      .then((dataUri) => {
        if (!cancelled) setLogoDataUri(dataUri);
      })
      .catch(() => {
        if (!cancelled) setLogoDataUri(mosquito);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const imageSettings = useMemo<LogoSettings | undefined>(() => {
    if (!withLogo || !logoDataUri) return undefined;
    const logoSize = Math.round(size * 0.22);
    return { src: logoDataUri, height: logoSize, width: logoSize, excavate: true };
  }, [withLogo, logoDataUri, size]);

  const qrProps = {
    value: value || ' ',
    size,
    level,
    fgColor,
    bgColor,
    marginSize: quietZone ? 4 : 0,
    imageSettings,
    title: value,
  };

  const downloadPng = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    triggerDownload(canvas.toDataURL('image/png'), `${fileNameFor(value)}.png`);
  }, [value]);

  const downloadSvg = useCallback(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const markup = new XMLSerializer().serializeToString(svg);
    const url = URL.createObjectURL(
      new Blob([`<?xml version="1.0" encoding="UTF-8"?>\n${markup}`], {
        type: 'image/svg+xml;charset=utf-8',
      })
    );
    triggerDownload(url, `${fileNameFor(value)}.svg`);
    URL.revokeObjectURL(url);
  }, [value]);

  const copyImage = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas || !navigator.clipboard || typeof ClipboardItem === 'undefined') return;
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!blob) return;
    await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
  }, []);

  // Firefox has no ClipboardItem, so the button is hidden rather than broken
  const canCopy =
    typeof window !== 'undefined' && typeof ClipboardItem !== 'undefined' && !!navigator.clipboard;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 5 }}>
        <Container maxWidth="md">
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
            <img src={mosquito} alt="Obesquito" width={44} height={44} />
            <Box>
              <Typography variant="h4" component="h1">
                QR Code Generator
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Internal Obesquito tool - this page is unlisted and not indexed.
              </Typography>
            </Box>
          </Stack>

          <Divider sx={{ my: 3 }} />

          <Grid container spacing={4}>
            <Grid item xs={12} md={7}>
              <Stack spacing={3}>
                <TextField
                  label="Link or text to encode"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  multiline
                  minRows={2}
                  maxRows={6}
                  fullWidth
                  helperText={`${value.length} characters`}
                />

                <Box>
                  <Typography gutterBottom>Size: {size}px</Typography>
                  <Slider
                    value={size}
                    min={128}
                    max={1024}
                    step={16}
                    // Slider hands back number | number[] because it also does ranges
                    onChange={(_event: Event, next: number | number[]) =>
                      setSize(Array.isArray(next) ? next[0] : next)
                    }
                    valueLabelDisplay="auto"
                  />
                </Box>

                <TextField
                  select
                  label="Error correction"
                  value={level}
                  // a select's value is typed as string, so narrow it back to the union
                  onChange={(e) => setLevel(e.target.value as ErrorCorrectionLevel)}
                  helperText="Higher levels stay scannable when the code is damaged or covered by the logo."
                >
                  {LEVELS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>

                <Stack direction="row" spacing={2}>
                  <TextField
                    label="Foreground"
                    type="color"
                    value={fgColor}
                    onChange={(e) => setFgColor(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Background"
                    type="color"
                    value={bgColor}
                    onChange={(e) => setBgColor(e.target.value)}
                    fullWidth
                  />
                </Stack>

                <Stack>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={quietZone}
                        onChange={(e) => setQuietZone(e.target.checked)}
                      />
                    }
                    label="Include quiet zone (recommended)"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox checked={withLogo} onChange={(e) => setWithLogo(e.target.checked)} />
                    }
                    label="Put the Obesquito mosquito in the middle"
                  />
                </Stack>
              </Stack>
            </Grid>

            <Grid item xs={12} md={5}>
              <Paper elevation={3} sx={{ p: 3 }}>
                <Stack spacing={2} alignItems="center">
                  <Box
                    sx={{
                      bgcolor: bgColor,
                      p: 1,
                      borderRadius: 1,
                      lineHeight: 0,
                      maxWidth: '100%',
                      '& canvas': { maxWidth: '100%', height: 'auto' },
                    }}
                  >
                    <QRCodeCanvas ref={canvasRef} {...qrProps} />
                  </Box>

                  <Button variant="contained" fullWidth onClick={downloadPng} disabled={!value}>
                    Download PNG
                  </Button>
                  <Button variant="outlined" fullWidth onClick={downloadSvg} disabled={!value}>
                    Download SVG
                  </Button>
                  {canCopy && (
                    <Button variant="text" fullWidth onClick={copyImage} disabled={!value}>
                      Copy to clipboard
                    </Button>
                  )}
                  <Typography variant="caption" color="text.secondary" align="center">
                    Scan the preview to check it before you print anything.
                  </Typography>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Rendered off screen purely so the SVG download matches the preview. */}
      <Box sx={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }} aria-hidden>
        <QRCodeSVG ref={svgRef} {...qrProps} xmlns="http://www.w3.org/2000/svg" />
      </Box>
    </ThemeProvider>
  );
}

export default QrCodeGeneratorPage;
